import { Badge } from "@/components/ui/badge";
import { Info } from "lucide-react";

import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";

export const money = (v, region) =>
  v == null ? "—" : new Intl.NumberFormat(region === "AUS" ? "en-AU" : "en-GB", {
    style: "currency", currency: region === "AUS" ? "AUD" : "GBP", maximumFractionDigits: 0,
  }).format(v);
export const moneyM = (v, region) => (v == null ? "—" : `${region === "AUS" ? "A$" : "£"}${(v / 1e6).toFixed(2)}m`);
export const pct = (v) => (v == null ? "—" : `${Number(v).toFixed(1)}%`);
export const mult = (v) => (v == null ? "—" : `${Number(v).toFixed(2)}x`);
export const num = (v) => (v == null ? "—" : new Intl.NumberFormat("en-GB").format(v));

export const OUTCOME_TONE = {
  "Paid clean": "bg-emerald-50 text-emerald-700 border-emerald-300",
  "Paid via renewal": "bg-teal-50 text-teal-700 border-teal-300",
  "Paid after restructure": "bg-lime-50 text-lime-800 border-lime-300",
  Performing: "bg-blue-50 text-blue-700 border-blue-300",
  "Not yet due": "bg-slate-50 text-slate-600 border-slate-300",
  "Early arrears": "bg-amber-50 text-amber-800 border-amber-300",
  "Serious arrears": "bg-orange-50 text-orange-800 border-orange-300",
  "Legal / Write-off": "bg-rose-50 text-rose-700 border-rose-300",
};

export function OutcomeBadge({ outcome }) {
  return (
    <Badge variant="outline" className={`text-[11px] whitespace-nowrap ${OUTCOME_TONE[outcome] || "bg-slate-100 text-slate-600"}`}>
      {outcome || "Unknown"}
    </Badge>
  );
}


// ── Mini diagrams for the (i) hover cards ────────────────────────────
const C = { ink: "#0f172a", mute: "#94a3b8", good: "#059669", bad: "#e11d48", warn: "#d97706", blue: "#2563eb", line: "#cbd5e1" };

function DiagRatio({ a, b, la, lb, ca = C.blue, cb = C.good }) {
  const max = Math.max(a, b, 1), w = 180;
  return (
    <svg width="200" height="52" className="mt-1">
      <text x="0" y="12" fontSize="9" fill={C.mute}>{la}</text><rect x="70" y="4" width={(a / max) * (w - 70)} height="10" rx="2" fill={ca} /><text x={74 + (a / max) * (w - 70)} y="12" fontSize="9" fill={C.ink}>{a}</text>
      <text x="0" y="36" fontSize="9" fill={C.mute}>{lb}</text><rect x="70" y="28" width={(b / max) * (w - 70)} height="10" rx="2" fill={cb} /><text x={74 + (b / max) * (w - 70)} y="36" fontSize="9" fill={C.ink}>{b}</text>
    </svg>
  );
}
function DiagShare({ n, of, color = C.bad }) {
  const cells = Array.from({ length: of });
  return (
    <div className="mt-1 flex flex-wrap gap-[3px] w-[190px]">{cells.map((_, i) => <span key={i} className="w-3 h-3 rounded-[2px]" style={{ background: i < n ? color : "#e2e8f0" }} />)}</div>
  );
}
function DiagTimeline({ marks, highlight }) {
  const w = 200;
  return (
    <svg width={w} height="46" className="mt-1">
      <line x1="6" y1="18" x2={w - 6} y2="18" stroke={C.line} strokeWidth="2" />
      {highlight && <rect x={highlight[0] * (w - 12) + 6} y="13" width={(highlight[1] - highlight[0]) * (w - 12)} height="10" fill={highlight[2] || C.good} opacity="0.25" />}
      {marks.map((m, i) => (
        <g key={i}><circle cx={m.at * (w - 12) + 6} cy="18" r="4" fill={m.color || C.blue} /><text x={m.at * (w - 12) + 6} y="38" fontSize="8.5" fill={C.ink} textAnchor={m.at < 0.15 ? "start" : m.at > 0.85 ? "end" : "middle"}>{m.label}</text></g>
      ))}
    </svg>
  );
}
function DiagChain({ items }) {
  return (
    <div className="mt-1 flex items-center gap-1 text-[10px]">
      {items.map((it, i) => (
        <span key={i} className="flex items-center gap-1">
          <span className="px-1.5 py-0.5 rounded border" style={{ borderColor: it.color || C.blue, color: it.color || C.blue, background: i === 0 ? "#eff6ff" : "#fff" }}>{it.label}</span>
          {i < items.length - 1 && <span className="text-slate-400">→</span>}
        </span>
      ))}
    </div>
  );
}
function DiagLadder({ steps }) {
  return (
    <div className="mt-1 flex items-end gap-1">
      {steps.map((st, i) => <div key={i} className="flex flex-col items-center gap-0.5"><div className="w-10 rounded-t" style={{ height: 10 + i * 8, background: st.color }} /><span className="text-[8.5px] text-slate-600 text-center leading-tight w-11">{st.label}</span></div>)}
    </div>
  );
}

// Plain-English help for every column / KPI header: text, an example, and a mini diagram.
export const HELP = {
  loans: { t: "Number of loan families. A family is an original loan plus any restructures of it.", e: "SF-681, SF-681/2 and SF-681/3 count as 1 loan.", d: <DiagChain items={[{ label: "SF-681" }, { label: "/2", color: C.warn }, { label: "/3", color: C.warn }]} /> },
  principal: { t: "Original loan amounts only. Restructures re-term an arrears balance and add no new money.", e: "£20,000 original + a £6,000 /2 re-term = £20,000 principal.", d: <DiagChain items={[{ label: "£20,000" }, { label: "/2 £0 new", color: C.mute }]} /> },
  avg_loan: { t: "Principal ÷ number of loans in the group.", e: "£1.0m across 40 loans = £25,000 average." },
  legal_wo_pct: { t: "Share of loans in the group whose latest link is in legal or written off.", e: "11 legal out of 100 loans = 11%.", d: <DiagShare n={11} of={100} /> },
  bad_pct: { t: "Share in serious arrears (30+ days past due) or legal / write-off.", e: "13 of 100 loans = 13% bad.", d: <DiagShare n={13} of={100} /> },
  ever_troubled_pct: { t: "Share that were restructured at any point, or are now bad. Early arrears alone does not count.", e: "24 of 100 = 24%.", d: <DiagShare n={24} of={100} color={C.warn} /> },
  settled_by_renewal_pct: { t: "Of the fully paid loans, the share cleared by a renewal (top-up) rather than by the borrower's cash.", e: "57 of 100 fully paid loans were renewed = 57%.", d: <DiagShare n={57} of={100} color={C.blue} /> },
  cash_multiple: { t: "Cash collected ÷ principal. 1.00x means the money came back with nothing on top.", e: "£13,900 collected on a £10,000 loan = 1.39x.", d: <DiagRatio a={100} b={139} la="Principal" lb="Cash in" /> },
  bad_balance_pct: { t: "Balance still owed on bad loans, as a share of all principal lent to the group.", e: "£150k owed on bad loans ÷ £1.0m lent = 15%.", d: <DiagRatio a={100} b={15} la="Lent" lb="Bad owed" cb={C.bad} /> },
  cash_by_day91_multiple: { t: "Cash collected in the first 91 days ÷ principal. Shows how fast money comes back.", e: "£8,900 by day 91 on £10,000 = 0.89x.", d: <DiagTimeline marks={[{ label: "Release", at: 0 }, { label: "Day 91", at: 0.5, color: C.good }, { label: "Maturity", at: 1, color: C.mute }]} highlight={[0, 0.5]} /> },
  never_paid: { t: "Loans with no repayment at all once the first payment was due plus 7 days.", e: "First payment due 8 Jun, nothing received by 15 Jun → Never paid.", d: <DiagTimeline marks={[{ label: "Release", at: 0 }, { label: "1st due", at: 0.45, color: C.warn }, { label: "+7d", at: 0.7, color: C.bad }]} highlight={[0.45, 0.7, C.warn]} /> },
  not_yet_due: { t: "Loans still inside their first-payment grace period, so no judgement yet.", e: "Released 3 days ago, first payment due next week." },
  outstanding_balance: { t: "What LoanDisk says is still owed on the latest link of the family.", e: "£4,392 balance on SF-170." },
  loans_funded: { t: "Every loan family ever funded in this region, whatever happened to it since.", e: "1,618 UK families." },
  principal_lent: { t: "Total original principal across every family.", e: "1,618 loans × £27k average ≈ £44m." },
  matured_book: { t: "Loans released at least 26 weeks (182 days) ago — old enough to judge how they performed.", e: "Released 1 Mar, as of 28 Sep → matured.", d: <DiagTimeline marks={[{ label: "Release", at: 0 }, { label: "26 weeks", at: 0.6, color: C.good }, { label: "Today", at: 1, color: C.mute }]} highlight={[0.6, 1]} /> },
  matured_bad: { t: "Bad rate on the matured book only, so young loans don't flatter the number.", e: "137 bad of 1,039 matured = 13.2%." },
  matured_multiple: { t: "Cash multiple on the matured book only.", e: "£38m collected on £27m = 1.39x.", d: <DiagRatio a={100} b={139} la="Principal" lb="Cash in" /> },
  settled_renewal: { t: "Matured loans fully paid via a renewal, as a share of matured fully paid loans.", e: "413 of 813 = 50.8%." },
  live_book: { t: "Every family not yet fully paid, grouped by how it is performing today.", e: "Performing, early arrears, serious arrears, legal." },
  base_sf: { t: "LoanDisk loan number of the original loan in the family.", e: "SF-681" },
  company_name: { t: "Borrower business name from LoanDisk.", e: "Haya Studios Ltd" },
  released_date: { t: "Date the original loan was paid out.", e: "28/07/2023" },
  release_quarter: { t: "Calendar quarter the original loan was released in.", e: "28 Jul 2023 → 2023 Q3" },
  matured: { t: "Released 182 or more days before the as-of date.", e: "Y / N" },
  product_name: { t: "LoanDisk loan product; tells us HO / Non-HO and debenture.", e: "FTLA, PG, Deb (HO)" },
  home_owner: { t: "Director is a home owner, read from the product name.", e: "(HO) → Y, (Non-HO) → N" },
  debenture: { t: "A debenture was taken, read from the product name.", e: "'Deb' in the name → Y" },
  repayment_cycle: { t: "How often repayments fall due.", e: "Daily, Weekly, Fortnightly, Monthly" },
  first_or_repeat: { t: "Whether this was the borrower's first loan with us or a repeat, and which number.", e: "Repeat (#3) = their third family.", d: <DiagChain items={[{ label: "#1 First" }, { label: "#2 Repeat", color: C.good }, { label: "#3 Repeat", color: C.good }]} /> },
  link_count: { t: "Number of links in the family. 1 means never restructured.", e: "SF-681 + /2 + /3 = 3 links." },
  latest_status: { t: "LoanDisk's own status on the latest link.", e: "Open, Fully Paid, Restructured, Legal" },
  outcome: { t: "Our single performance label for the family, taken from the latest link.", e: "Open + 12 days past due → Early arrears.", d: <DiagLadder steps={[{ label: "Performing", color: C.blue }, { label: "Early 1–30d", color: C.warn }, { label: "Serious 30d+", color: "#ea580c" }, { label: "Legal / W-O", color: C.bad }]} /> },
  cash_collected: { t: "All repayments across the whole family, excluding Credit Memo and Written Off entries. Renewals count.", e: "£15,347 on SF-170 + £2,000 on /2 = £17,347." },
  cash_by_day91: { t: "Cash in the first 91 days as a multiple of principal.", e: "0.89x" },
  days_past_due: { t: "LoanDisk's days past due on the latest link.", e: "941" },
  pct_term_first_behind: { t: "How far through the term the loan first fell more than 25% behind its repayment schedule. Measured from first payment + 7 days grace.", e: "Fell behind on day 70 of a 273-day term → 25.6%.", d: <DiagTimeline marks={[{ label: "1st due", at: 0.1, color: C.warn }, { label: "25% behind", at: 0.45, color: C.bad }, { label: "Maturity", at: 1, color: C.mute }]} highlight={[0.1, 0.45, C.bad]} /> },
  settled_by_renewal: { t: "Fully paid with at least one Renewal repayment in the family.", e: "Y / N" },
  dq_flags: { t: "Data quality flags — things we could not derive or that look unusual. Hover the count to read them.", e: "'non-standard SF reference'" },
  child_status: { t: "LoanDisk sub-status of an open loan.", e: "Current, Due Today, Missed Repayment, Arrears, Past Maturity" },
  "dr.report_found": { t: "Whether a Creditsafe director report dated on or before release was found on the monday.com case.", e: "Y / N; 'not scanned' until Phase 2 has run for this loan." },
  "dr.worst_consumer_score": { t: "Lowest Creditsafe consumer score across the directors with a report.", e: "Two directors scoring 578 and 712 → 578." },
  "dr.worst_band": { t: "Band of the worst-scoring director (1 = worst, 10 = best).", e: "578 → Band 8" },
  "dr.total_active_ccjs": { t: "Active county court judgments across all directors with a report.", e: "0" },
  "dr.total_active_ccj_value": { t: "Total value of those active CCJs.", e: "£0" },
  "dr.total_advance_against_income_12m": { t: "Payday-style 'Advance Against Income' credit searches in the 12 months before the report, all directors.", e: "4 searches worth £5,000." },
  "dr.max_searches_12m": { t: "Highest 'searches in last 12 months' figure among the directors.", e: "10" },
  "dr.any_director_not_on_electoral_roll": { t: "Y if any director's current address was not confirmed on the Electoral Roll.", e: "Y / N" },
  "dr.any_debt_collection_link": { t: "Y if any director has an address link sourced from a Debt Collection Agency.", e: "Y / N" },
  "dr.min_years_at_address": { t: "Shortest time at current address among the directors, in years.", e: "2.5" },
};

export function H({ k, children, className = "" }) {
  const help = HELP[k];
  if (!help) return <span className={className}>{children}</span>;
  return (
    <span className={`inline-flex items-center gap-1 ${className}`}>
      {children}
      <HoverCard openDelay={150} closeDelay={80}>
        <HoverCardTrigger asChild>
          <button type="button" className="inline-flex text-slate-400 hover:text-emerald-600 cursor-help" data-testid={`help-${k}`} aria-label="What does this mean?"><Info className="w-3 h-3" /></button>
        </HoverCardTrigger>
        <HoverCardContent side="top" align="start" collisionPadding={16} className="w-64 p-3 text-left normal-case tracking-normal font-normal" data-testid={`help-card-${k}`}>
          <p className="text-xs text-slate-800 leading-snug">{help.t}</p>
          {help.e && <p className="text-[11px] text-slate-500 mt-1.5"><span className="font-semibold text-slate-600">Example: </span>{help.e}</p>}
          {help.d}
        </HoverCardContent>
      </HoverCard>
    </span>
  );
}

// Shared metric columns for Vintages / Segments tables
export const METRIC_COLS = [
  { key: "loans", label: "Loans", fmt: num },
  { key: "principal", label: "Principal", fmt: moneyM, money: true },
  { key: "avg_loan", label: "Avg loan", fmt: money, money: true },
  { key: "legal_wo_pct", label: "Legal / W-O %", fmt: pct, heat: "bad" },
  { key: "bad_pct", label: "Bad %", fmt: pct, heat: "bad" },
  { key: "ever_troubled_pct", label: "Ever troubled %", fmt: pct, heat: "bad" },
  { key: "settled_by_renewal_pct", label: "Settled by renewal %", fmt: pct },
  { key: "cash_multiple", label: "Cash multiple", fmt: mult, heat: "good" },
  { key: "bad_balance_pct", label: "Bad balance % principal", fmt: pct, heat: "bad" },
  { key: "cash_by_day91_multiple", label: "Cash by day 91", fmt: mult },
];

export function heatClass(col, v) {
  if (v == null || !col.heat) return "";
  if (col.heat === "bad") return v >= 20 ? "text-rose-700 font-semibold" : v >= 10 ? "text-amber-700" : "text-emerald-700";
  return v >= 1.3 ? "text-emerald-700 font-semibold" : v >= 1.0 ? "text-slate-800" : "text-rose-700";
}

export function MetricTable({ rows, total, region, firstCol = "Group", testid, extra, showEarlyBad = false }) {
  return (
    <div className="overflow-x-auto rounded-md border border-slate-200" data-testid={testid}>
      <table className="w-full text-xs">
        <thead className="bg-slate-50 text-[10px] uppercase tracking-wide text-slate-500">
          <tr>
            <th className="px-3 py-2 text-left font-semibold">{firstCol}</th>
            {METRIC_COLS.map((c) => <th key={c.key} className="px-3 py-2 text-right font-semibold whitespace-nowrap"><H k={c.key}>{c.label}</H></th>)}
            {showEarlyBad && <th className="px-3 py-2 text-right font-semibold whitespace-nowrap" title="Share of loans that went bad and first fell >25% behind within the first quarter of the term">Bad in 1st qtr %</th>}
            {extra && <th className="px-3 py-2 text-right font-semibold">{extra.label}</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.group} className={`border-t border-slate-100 hover:bg-emerald-50/40 ${r.excluded ? "text-slate-400 italic" : ""}`} data-testid={`${testid}-row-${String(r.group).replace(/\s+/g, "-")}`}>
              <td className="px-3 py-1.5 font-medium text-slate-800 whitespace-nowrap">{r.excluded ? <span className="text-slate-500" title="In scope but no source data — shown so the table adds up to the Total">{r.group}</span> : r.group}</td>
              {METRIC_COLS.map((c) => {
                const isBad = ["legal_wo_pct", "bad_pct", "ever_troubled_pct", "bad_balance_pct"].includes(c.key);
                if (isBad && r.too_few) return <td key={c.key} className="px-3 py-1.5 text-right text-slate-400 italic" title="Fewer than 50 loans or fewer than 10 bad loans in this group">too few to judge</td>;
                return (
                  <td key={c.key} className={`px-3 py-1.5 text-right tabular-nums ${heatClass(c, r[c.key])}`}>
                    {c.fmt(r[c.key], region)}
                    {c.key === "bad_pct" && r.bad_ci95 && <span className="block text-[10px] text-slate-400 font-normal" title="95% Wilson range">{r.bad_ci95[0]}–{r.bad_ci95[1]}%</span>}
                  </td>
                );
              })}
              {showEarlyBad && <td className="px-3 py-1.5 text-right tabular-nums">{pct(r.early_bad_pct)}</td>}
              {extra && <td className="px-3 py-1.5 text-right">{extra.render(r)}</td>}
            </tr>
          ))}
          {total && (
            <tr className="border-t-2 border-slate-300 bg-slate-50 font-semibold">
              <td className="px-3 py-1.5">Total</td>
              {METRIC_COLS.map((c) => <td key={c.key} className="px-3 py-1.5 text-right tabular-nums">{c.fmt(total[c.key], region)}</td>)}
              {showEarlyBad && <td />}
              {extra && <td />}
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

export function Kpi({ label, value, sub, tone = "default", testid, help }) {
  const tones = { default: "border-slate-200", good: "border-emerald-300 bg-emerald-50/40", bad: "border-rose-300 bg-rose-50/40" };
  return (
    <div className={`rounded-lg border p-3 ${tones[tone]}`} data-testid={testid}>
      <p className="text-[10px] uppercase tracking-wide text-slate-500"><H k={help}>{label}</H></p>
      <p className="text-xl font-bold text-slate-900 tabular-nums" style={{ fontFamily: "Manrope, sans-serif" }}>{value}</p>
      {sub && <p className="text-[11px] text-slate-500 mt-0.5">{sub}</p>}
    </div>
  );
}
